/*
 * Draggable infinite slider — Osmo / GSAP setup.
 * https://www.osmo.supply/resource/draggable-infinite-slider-with-gsap
 *
 * Shared slider engine (used by the product slider AND the reviews slider). Uses the official GSAP
 * horizontalLoop() helper + Draggable + InertiaPlugin. Each <wp-slider> (display: contents) wraps a
 * [data-slider-root] and loops its [data-slider="slide"] items; optional
 * [data-slider-button="prev"|"next"] arrows call loop.previous()/next(). Slide widths (how many show
 * per view) are set per section in CSS.
 *
 * Built in connectedCallback inside a gsap.matchMedia() and reverted in disconnectedCallback, so a
 * theme-editor re-render starts clean. The slider is functional, so it runs under reduced motion too —
 * but there the arrows jump instantly and a drag snaps to the nearest slide with no inertia throw.
 * Draggable + InertiaPlugin are loaded per-section just before this file (after smooth-scroll.js
 * has run), so they're registered here.
 */
(function () {
  if (!window.gsap || typeof window.Draggable === 'undefined' || customElements.get('wp-slider')) return;
  gsap.registerPlugin(Draggable);
  if (window.InertiaPlugin) gsap.registerPlugin(InertiaPlugin);

  /* GSAP official helper: seamless, optionally-draggable horizontal loop.
     https://gsap.com/docs/v3/HelperFunctions/helpers/seamlessLoop/
     Adapted: no inner gsap.context() — it's always called inside the element's matchMedia, which
     records everything (a nested context wouldn't be reverted with it); the resize listener is
     removed via tl.cleanup(); resize is debounced and ignores height-only changes (mobile URL bar);
     config.inertia: false drops the throw and snaps to the closest slide on release. */
  function horizontalLoop(items, config) {
    items = gsap.utils.toArray(items);
    config = config || {};
    {
      let onChange = config.onChange,
        lastIndex = 0,
        tl = gsap.timeline({
          repeat: config.repeat,
          onUpdate:
            onChange &&
            function () {
              let i = tl.closestIndex();
              if (lastIndex !== i) {
                lastIndex = i;
                onChange(items[i], i);
              }
            },
          paused: config.paused,
          defaults: { ease: 'none' },
          onReverseComplete: () => tl.totalTime(tl.rawTime() + tl.duration() * 100),
        }),
        length = items.length,
        startX = items[0].offsetLeft,
        times = [],
        widths = [],
        spaceBefore = [],
        xPercents = [],
        curIndex = 0,
        indexIsDirty = false,
        center = config.center,
        pixelsPerSecond = (config.speed || 1) * 100,
        snap = config.snap === false ? (v) => v : gsap.utils.snap(config.snap || 1),
        timeOffset = 0,
        container = center === true ? items[0].parentNode : gsap.utils.toArray(center)[0] || items[0].parentNode,
        totalWidth,
        getTotalWidth = () =>
          items[length - 1].offsetLeft +
          (xPercents[length - 1] / 100) * widths[length - 1] -
          startX +
          spaceBefore[0] +
          items[length - 1].offsetWidth * gsap.getProperty(items[length - 1], 'scaleX') +
          (parseFloat(config.paddingRight) || 0),
        populateWidths = () => {
          let b1 = container.getBoundingClientRect(),
            b2;
          items.forEach((el, i) => {
            widths[i] = parseFloat(gsap.getProperty(el, 'width', 'px'));
            xPercents[i] = snap(
              (parseFloat(gsap.getProperty(el, 'x', 'px')) / widths[i]) * 100 + gsap.getProperty(el, 'xPercent'),
            );
            b2 = el.getBoundingClientRect();
            spaceBefore[i] = b2.left - (i ? b1.right : b1.left);
            b1 = b2;
          });
          gsap.set(items, { xPercent: (i) => xPercents[i] });
          totalWidth = getTotalWidth();
        },
        timeWrap,
        populateOffsets = () => {
          timeOffset = center ? (tl.duration() * (container.offsetWidth / 2)) / totalWidth : 0;
          center &&
            times.forEach((t, i) => {
              times[i] = timeWrap(tl.labels['label' + i] + (tl.duration() * widths[i]) / 2 / totalWidth - timeOffset);
            });
        },
        getClosest = (values, value, wrap) => {
          let i = values.length,
            closest = 1e10,
            index = 0,
            d;
          while (i--) {
            d = Math.abs(values[i] - value);
            if (d > wrap / 2) d = wrap - d;
            if (d < closest) {
              closest = d;
              index = i;
            }
          }
          return index;
        },
        populateTimeline = () => {
          let i, item, curX, distanceToStart, distanceToLoop;
          tl.clear();
          for (i = 0; i < length; i++) {
            item = items[i];
            curX = (xPercents[i] / 100) * widths[i];
            distanceToStart = item.offsetLeft + curX - startX + spaceBefore[0];
            distanceToLoop = distanceToStart + widths[i] * gsap.getProperty(item, 'scaleX');
            tl.to(
              item,
              {
                xPercent: snap(((curX - distanceToLoop) / widths[i]) * 100),
                duration: distanceToLoop / pixelsPerSecond,
              },
              0,
            )
              .fromTo(
                item,
                {
                  xPercent: snap(((curX - distanceToLoop + totalWidth) / widths[i]) * 100),
                },
                {
                  xPercent: xPercents[i],
                  duration: (curX - distanceToLoop + totalWidth - curX) / pixelsPerSecond,
                  immediateRender: false,
                },
                distanceToLoop / pixelsPerSecond,
              )
              .add('label' + i, distanceToStart / pixelsPerSecond);
            times[i] = distanceToStart / pixelsPerSecond;
          }
          timeWrap = gsap.utils.wrap(0, tl.duration());
        },
        refresh = (deep) => {
          let progress = tl.progress();
          tl.progress(0, true);
          populateWidths();
          deep && populateTimeline();
          populateOffsets();
          deep && tl.draggable && tl.paused() ? tl.time(times[curIndex], true) : tl.progress(progress, true);
        },
        lastWidth = window.innerWidth,
        resizeTimer,
        onResize = () => {
          if (window.innerWidth === lastWidth) return;
          lastWidth = window.innerWidth;
          clearTimeout(resizeTimer);
          resizeTimer = setTimeout(() => refresh(true), 150);
        },
        useInertia = config.inertia !== false,
        proxy;
      gsap.set(items, { x: 0 });
      populateWidths();
      populateTimeline();
      populateOffsets();
      window.addEventListener('resize', onResize);
      function toIndex(index, vars) {
        vars = vars || {};
        Math.abs(index - curIndex) > length / 2 && (index += index > curIndex ? -length : length);
        let newIndex = gsap.utils.wrap(0, length, index),
          time = times[newIndex];
        if (time > tl.time() !== index > curIndex && index !== curIndex) {
          time += tl.duration() * (index > curIndex ? 1 : -1);
        }
        if (time < 0 || time > tl.duration()) {
          vars.modifiers = { time: timeWrap };
        }
        curIndex = newIndex;
        vars.overwrite = true;
        gsap.killTweensOf(proxy);
        return vars.duration === 0 ? tl.time(timeWrap(time)) : tl.tweenTo(time, vars);
      }
      tl.toIndex = (index, vars) => toIndex(index, vars);
      tl.closestIndex = (setCurrent) => {
        let index = getClosest(times, tl.time(), tl.duration());
        if (setCurrent) {
          curIndex = index;
          indexIsDirty = false;
        }
        return index;
      };
      tl.current = () => (indexIsDirty ? tl.closestIndex(true) : curIndex);
      tl.next = (vars) => toIndex(tl.current() + 1, vars);
      tl.previous = (vars) => toIndex(tl.current() - 1, vars);
      tl.times = times;
      tl.progress(1, true).progress(0, true); // pre-render for performance
      if (config.reversed) {
        tl.vars.onReverseComplete();
        tl.reverse();
      }
      if (config.draggable && typeof Draggable === 'function') {
        proxy = document.createElement('div');
        let wrap = gsap.utils.wrap(0, 1),
          ratio,
          startProgress,
          draggable,
          lastSnap,
          initChangeX,
          align = () => tl.progress(wrap(startProgress + (draggable.startX - draggable.x) * ratio)),
          syncIndex = () => tl.closestIndex(true);
        draggable = Draggable.create(proxy, {
          trigger: items[0].parentNode,
          type: 'x',
          onPressInit() {
            let x = this.x;
            gsap.killTweensOf(tl);
            tl.pause();
            startProgress = tl.progress();
            refresh();
            ratio = 1 / totalWidth;
            initChangeX = startProgress / -ratio - x;
            gsap.set(proxy, { x: startProgress / -ratio });
          },
          onDrag: align,
          onThrowUpdate: align,
          overshootTolerance: 0,
          inertia: useInertia,
          snap(value) {
            if (Math.abs(startProgress / -ratio - this.x) < 10) return lastSnap + initChangeX;
            let time = -(value * ratio) * tl.duration(),
              wrappedTime = timeWrap(time),
              snapTime = times[getClosest(times, wrappedTime, tl.duration())],
              dif = snapTime - wrappedTime;
            Math.abs(dif) > tl.duration() / 2 && (dif += dif < 0 ? tl.duration() : -tl.duration());
            lastSnap = (time + dif) / tl.duration() / -ratio;
            return lastSnap;
          },
          onRelease() {
            syncIndex();
            draggable.isThrowing && (indexIsDirty = true);
            // No throw, so snap() never runs — jump to the nearest slide instead.
            useInertia || tl.toIndex(tl.closestIndex(true), { duration: 0 });
          },
          onThrowComplete: syncIndex,
        })[0];
        tl.draggable = draggable;
      }
      tl.closestIndex(true);
      lastIndex = curIndex;
      onChange && onChange(items[curIndex], curIndex);
      tl.cleanup = () => {
        window.removeEventListener('resize', onResize);
        clearTimeout(resizeTimer);
        gsap.killTweensOf(tl);
        proxy && gsap.killTweensOf(proxy);
        tl.draggable && tl.draggable.kill();
      };
      return tl;
    }
  }

  const build = (root, reduceMotion) => {
    const slides = root.querySelectorAll('[data-slider="slide"]');
    if (slides.length < 2) return;

    const list = root.querySelector('[data-slider="list"]');
    const gap = parseFloat(getComputedStyle(list).columnGap) || 0;

    const loop = horizontalLoop(slides, {
      paused: true,
      draggable: true,
      inertia: !reduceMotion,
      center: false,
      snap: 1,
      repeat: -1,
      paddingRight: gap, // trailing gutter so wrapped cards keep the gap
    });

    const prev = root.querySelector('[data-slider-button="prev"]');
    const next = root.querySelector('[data-slider-button="next"]');
    const move = reduceMotion ? { duration: 0 } : { duration: 0.725, ease: 'power3' };
    // toIndex() mutates the vars it's given, so pass a fresh copy each click.
    const onPrev = () => loop.previous({ ...move });
    const onNext = () => loop.next({ ...move });
    if (prev) prev.addEventListener('click', onPrev);
    if (next) next.addEventListener('click', onNext);

    return () => {
      if (prev) prev.removeEventListener('click', onPrev);
      if (next) next.removeEventListener('click', onNext);
      loop.cleanup();
    };
  };

  class WpSlider extends HTMLElement {
    connectedCallback() {
      const root = this.querySelector('[data-slider-root]');
      if (!root) return;

      // Measure after layout is settled (images + fonts) so the loop positions are correct; a
      // theme-editor re-render (document already complete) builds straight away.
      this.init = () => {
        this.init = null;
        this.mm = gsap.matchMedia();
        this.mm.add(
          {
            motion: '(prefers-reduced-motion: no-preference)',
            reduce: '(prefers-reduced-motion: reduce)',
          },
          (context) => build(root, context.conditions.reduce),
        );
      };
      if (document.readyState === 'complete') this.init();
      else window.addEventListener('load', this.init, { once: true });
    }

    disconnectedCallback() {
      if (this.init) window.removeEventListener('load', this.init);
      this.init = null;
      if (this.mm) this.mm.revert();
      this.mm = null;
    }
  }

  customElements.define('wp-slider', WpSlider);
})();
